import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useSetAtom, useAtomValue } from 'jotai';
import { atom } from 'jotai';
import { useCallback } from 'react';
import { QUERY_KEYS } from '@restworld/utils-common';
import { useJobPositionsService, useEmployersService } from '@restworld/data-services';
import { useTranslation } from 'next-i18next';
import { showToast } from '@restworld/ui-ds';
import { useAuth } from '@restworld/feature-authentication';
import {
  useIsSubscriptionFeedbacksSubmitted,
  useFetchIsSubscriptionFeedbacksSubmittedQuery,
} from '../../flexible-slots-v2/flexible-slots-v2.hooks';

export const putOfflineJobPositionConfirmationModalAtom = atom<boolean>(false);
export const putOfflineJobPositionNPSModalAtom = atom<boolean>(false);
export const putOfflineJobPositionSuccessModalAtom = atom<boolean>(false);
export const jobPositionIdToPutOfflineAtom = atom<string | null>('');

export interface NPSFeedbackData {
  npsScore: number;
  motiveForCancelling?: string;
  reactivationDate?: string;
}

export function usePutOfflineJobPosition() {
  const queryClient = useQueryClient();
  const jobPositionsService = useJobPositionsService();
  const employersService = useEmployersService();
  const { t } = useTranslation('job-positions-page');
  const { user } = useAuth();

  const setIsConfirmationModalOpen = useSetAtom(putOfflineJobPositionConfirmationModalAtom);
  const setIsNPSModalOpen = useSetAtom(putOfflineJobPositionNPSModalAtom);
  const setIsSuccessModalOpen = useSetAtom(putOfflineJobPositionSuccessModalAtom);
  const setJpIdToPutOffline = useSetAtom(jobPositionIdToPutOfflineAtom);
  const isConfirmationModalOpen = useAtomValue(putOfflineJobPositionConfirmationModalAtom);
  const isNPSModalOpen = useAtomValue(putOfflineJobPositionNPSModalAtom);
  const isSuccessModalOpen = useAtomValue(putOfflineJobPositionSuccessModalAtom);
  const jpIdToPutOffline = useAtomValue(jobPositionIdToPutOfflineAtom);

  // Fetch subscription feedback status when confirmation modal is open
  useFetchIsSubscriptionFeedbacksSubmittedQuery();

  const isSubscriptionFeedbacksSubmitted = useIsSubscriptionFeedbacksSubmitted();

  // Submit NPS feedback mutation
  const { mutate: submitNPSFeedback, isPending: isSubmittingFeedback } = useMutation({
    mutationFn: async (feedbackData: NPSFeedbackData) => {
      return employersService.createEmployerSubscriptionFeedback({
        organization_id: user?.organization_id,
        service_referral_rating: feedbackData.npsScore.toString(),
        motive_for_cancelling_subscription: feedbackData.motiveForCancelling,
        reactivate_service: feedbackData.reactivationDate,
      });
    },
    onSuccess: () => {
      // After submitting feedback, proceed to put job offline
      if (jpIdToPutOffline) {
        putOfflineJobPosition({ jobPositionId: jpIdToPutOffline });
      }
    },
    onError: () => {
      showToast(t('nps_feedback_error_message'), 'error', {
        position: 'top-right',
        autoClose: 3000,
      });
    },
  });

  const { mutate: putOfflineJobPosition, isPending: isPuttingOffline } = useMutation({
    mutationFn: ({ jobPositionId }: { jobPositionId: string }) =>
      jobPositionsService.putOfflineJobPosition(jobPositionId),
    onSuccess: () => {
      // Invalidate the job positions list query to refresh the data
      queryClient.invalidateQueries({
        queryKey: [QUERY_KEYS.JOB_POSITIONS.LIST],
      });
      // Close all modals and show success
      setIsConfirmationModalOpen(false);
      setIsNPSModalOpen(false);
      setIsSuccessModalOpen(true);
    },
    onError: () => {
      // Show error toast with high z-index to appear above modal
      showToast(t('put_offline_error_message'), 'error', {
        position: 'top-right',
        autoClose: 3000,
      });
      // Close modals on error
      setIsConfirmationModalOpen(false);
      setIsNPSModalOpen(false);
    },
  });

  const handleOpenPutOffline = useCallback(
    (jpId: string) => {
      setJpIdToPutOffline(jpId);
      setIsConfirmationModalOpen(true);
    },
    [setJpIdToPutOffline, setIsConfirmationModalOpen]
  );

  // Step 1: User confirms they want to put offline
  const handleConfirmPutOffline = useCallback(() => {
    // Check if we should skip NPS (already submitted feedback recently)
    if (isSubscriptionFeedbacksSubmitted) {
      // Skip NPS, go directly to put offline
      if (jpIdToPutOffline) {
        putOfflineJobPosition({ jobPositionId: jpIdToPutOffline });
      }
    } else {
      // Show NPS modal (Step 2)
      setIsConfirmationModalOpen(false);
      setIsNPSModalOpen(true);
    }
  }, [
    isSubscriptionFeedbacksSubmitted,
    jpIdToPutOffline,
    putOfflineJobPosition,
    setIsConfirmationModalOpen,
    setIsNPSModalOpen,
  ]);

  // Step 2: User submits NPS feedback
  const handleSubmitNPSFeedback = useCallback(
    (feedbackData: NPSFeedbackData) => {
      submitNPSFeedback(feedbackData);
    },
    [submitNPSFeedback]
  );

  const handleCloseConfirmationModal = useCallback(() => {
    if (!isPuttingOffline) {
      setIsConfirmationModalOpen(false);
      setJpIdToPutOffline(null);
    }
  }, [isPuttingOffline, setIsConfirmationModalOpen, setJpIdToPutOffline]);

  const handleCloseNPSModal = useCallback(() => {
    if (!isSubmittingFeedback && !isPuttingOffline) {
      setIsNPSModalOpen(false);
      setJpIdToPutOffline(null);
    }
  }, [isSubmittingFeedback, isPuttingOffline, setIsNPSModalOpen, setJpIdToPutOffline]);

  const handleCloseSuccessModal = useCallback(() => {
    const jobPositionId = jpIdToPutOffline;
    setIsSuccessModalOpen(false);
    setJpIdToPutOffline(null);
    if (jobPositionId) {
      void queryClient.invalidateQueries({
        queryKey: [QUERY_KEYS.JOB_POSITIONS.DETAILS, jobPositionId],
        exact: true,
      });
    }
  }, [jpIdToPutOffline, queryClient, setIsSuccessModalOpen, setJpIdToPutOffline]);

  return {
    isPuttingOffline,
    isSubmittingFeedback,
    isConfirmationModalOpen,
    isNPSModalOpen,
    isSuccessModalOpen,
    jpIdToPutOffline,
    isSubscriptionFeedbacksSubmitted,
    handleOpenPutOffline,
    handleConfirmPutOffline,
    handleSubmitNPSFeedback,
    handleCloseConfirmationModal,
    handleCloseNPSModal,
    handleCloseSuccessModal,
  };
}
